// FeaturesSection.jsx
import React, { useState } from "react";
import {
  Monitor,
  Video,
  Laptop,
  MessageSquare,
  Users,
  CheckSquare,
  BookOpen,
  TrendingUp,
  Briefcase,
  Download,
} from "lucide-react";

const WhyChoseUs = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const GOLD_GRADIENT = "linear-gradient(90deg, #fc8650, #f58752, #d96a36)";
  const GOLD_COLOR = "#f58752";
  const GOLD_BORDER = "#f58752";

  const features = [
    {
      icon: Monitor,
      title: "Offline Trainings",
      description: "Immerse in expert-led sessions",
      number: "01",
    },
    {
      icon: Video,
      title: "Live Online Classes",
      description: "Interactive learning from home",
      number: "02",
    },
    {
      icon: Video,
      title: "Daily Class Recordings",
      description: "Revise with ease anytime",
      number: "03",
    },
    {
      icon: Laptop,
      title: "Pre-recorded Videos",
      description: "Flexible learning on demand",
      number: "04",
    },
    {
      icon: MessageSquare,
      title: "Group Discussions",
      description: "Collaborate to enhance skills",
      number: "05",
    },
    {
      icon: Users,
      title: "5k+ Students Community",
      description: "Join a thriving learning network",
      number: "06",
    },
    {
      icon: CheckSquare,
      title: "Teams & Group Tasks",
      description: "Master real-world skills",
      number: "07",
    },
    {
      icon: BookOpen,
      title: "Live Course Revision",
      description: "Refine skills with live support",
      number: "08",
    },
    {
      icon: CheckSquare,
      title: "Assignments & Projects",
      description: "Hands-on industry preparation",
      number: "09",
    },
    {
      icon: TrendingUp,
      title: "Individual Feedback",
      description: "Personalized growth insights",
      number: "10",
    },
    {
      icon: Briefcase,
      title: "Placement Trainings",
      description: "Step confidently into your career",
      number: "11",
    },
    {
      icon: Download,
      title: "Downloadable Content",
      description: "Rich resources at your fingertips",
      number: "12",
    },
  ];

  const visibleFeatures = showAll ? features : features.slice(0, 3);

  return (
    <div style={{ minHeight: "90vh", backgroundColor: "#ffffff" }}>
      <section
        id="features"
        className="py-5"
        style={{ backgroundColor: "#ffffff" }}
      >
        <div className="container" style={{ margin: "0 auto" }}>
          <div className="text-center mb-5">
            {/* <span
              className="badge mb-3"
              style={{
                background: GOLD_GRADIENT,
                color: "#7a5a00",
                fontSize: "1.3rem",
                padding: "8px 16px",
                borderRadius: "50px",
                fontWeight: "600",
              }}
            >
              Why Choose Us
            </span> */}
            <h2
              className="display-4 fw-bold mb-4"
              style={{ color: "#1f2937", letterSpacing: "-0.02em" }}
            >
              12 Powerful Features for Your Success
            </h2>
            <p
              className="fs-5 mb-0"
              style={{
                color: "#6b7280",
                maxWidth: "760px",
                margin: "0 auto",
                lineHeight: "1.6",
              }}
            >
              Comprehensive tools and resources designed to accelerate your
              learning journey and achieve your goals
            </p>
          </div>

          <div
            className="row g-4"
            style={{
              display: "flex",
              gap: 20,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {visibleFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              // compute the absolute index in the full list for hover and keys
              const absoluteIndex = showAll ? index : index; // no change needed, using map index is fine here for keys
              return (
                <div
                  key={absoluteIndex}
                  className="col-12 col-md-6 col-lg-4"
                  style={{
                    flex: "0 0 calc(33.333% - 20px)",
                    maxWidth: "400px",
                  }}
                >
                  <div
                    className="feature-card"
                    onMouseEnter={() => setHoveredCard(absoluteIndex)}
                    onMouseLeave={() => setHoveredCard(null)}
                    style={{
                      backgroundColor: "#ffffff",
                      border:
                        hoveredCard === absoluteIndex
                          ? `2px solid ${GOLD_BORDER}`
                          : "2px solid #f3f4f6",
                      borderRadius: "20px",
                      padding: "36px 28px",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden",
                      boxShadow:
                        hoveredCard === absoluteIndex
                          ? "0 20px 40px rgba(255, 193, 7, 0.15)"
                          : "0 4px 6px rgba(0, 0, 0, 0.05)",
                      transform:
                        hoveredCard === absoluteIndex
                          ? "translateY(-8px)"
                          : "translateY(0)",
                    }}
                  >
                    <div style={{ position: "relative", zIndex: 1 }}>
                      <div
                        className="d-flex justify-content-between align-items-start mb-4"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <div
                          style={{
                            background:
                              hoveredCard === absoluteIndex
                                ? "linear-gradient(90deg, rgba(255,215,0,0.15), rgba(255,193,7,0.08))"
                                : "#fff9ec",
                            borderRadius: "16px",
                            padding: "16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.3s ease",
                            transform:
                              hoveredCard === absoluteIndex
                                ? "scale(1.07) rotate(4deg)"
                                : "scale(1) rotate(0deg)",
                          }}
                        >
                          <IconComponent
                            size={28}
                            style={{ color: GOLD_COLOR }}
                          />
                        </div>

                        <span
                          style={{
                            color: "#e95f19",
                            fontSize: "2.5rem",
                            fontWeight: "800",
                            transition: "all 0.3s ease",
                            opacity: 0.7,
                          }}
                        >
                          {feature.number}
                        </span>
                      </div>

                      <h5
                        className="fw-bold mb-3"
                        style={{
                          color: "#1f2937",
                          fontSize: "1.3rem",
                          lineHeight: "1.3",
                        }}
                      >
                        {feature.title}
                      </h5>
                      <p
                        className="mb-4"
                        style={{
                          color: "#6b7280",
                          fontSize: "1rem",
                          lineHeight: "1.6",
                        }}
                      >
                        {feature.description}
                      </p>
                    </div>

                    <div
                      className="feature-card-gradient"
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "6px",
                        background: GOLD_GRADIENT,
                        opacity: hoveredCard === absoluteIndex ? 1 : 0,
                        transition: "opacity 0.3s ease",
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-4">
            <button
              onClick={() => setShowAll((prev) => !prev)}
              className="btn"
              style={{
                background: showAll ? "transparent" : GOLD_GRADIENT,
                color: showAll ? "#1f2937" : "#fff",
                border: `1px solid ${GOLD_BORDER}`,
                padding: "10px 20px",
                borderRadius: "999px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.25s ease",
              }}
            >
              {showAll ? "Show less" : `Show more (${features.length - 3})`}
            </button>
          </div>
        </div>
      </section>
      <style>{`
        * { box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial; margin:0; padding:0; -webkit-font-smoothing:antialiased; -moz-osx-font-smoothing:grayscale; }
        a { color: inherit; }
        .container { padding-left: 18px; padding-right: 18px; }
        @media (max-width: 1000px) {
          .row.g-4 { gap: 20px !important; }
          .col-12.col-md-6.col-lg-4 { flex: 0 0 100% !important; max-width: 90% !important; }
        }
      `}</style>
    </div>
  );
}
export default WhyChoseUs;